import type { Module, Pointer } from '@phi/jcore/module';
// @ts-ignore
import * as jcore_cb from '../core_callbacks';
// @ts-ignore
import { debug_additional_initializations } from '../debug';
import { Edge, Face, Vertex } from '@phi/jcore';

export default (Module: Module) => {
    // Install callbacks
    Module.ccall('phi_install_display_log_msg', 'number', ['number'], [
        Module.addFunction(jcore_cb.display_log_message, 'vii')
    ]);
    Module.ccall('phi_install_display_modify_point', 'number', ['number'], [
        Module.addFunction(jcore_cb.modify_point, 'iifff')
    ]);
    Module.ccall('phi_install_display_modify_polyline', 'number', ['number'], [
        Module.addFunction(jcore_cb.modify_polyline, 'iiii')
    ]);
    Module.ccall('phi_install_display_modify_face', 'number', ['number'], [
        Module.addFunction(jcore_cb.modify_face, 'iiii')
    ]);
    Module.ccall('phi_install_display_add_point', 'number', ['number'], [
        Module.addFunction(jcore_cb.add_point, 'iifff')
    ]);
    Module.ccall('phi_install_display_add_polyline', 'number', ['number'], [
        Module.addFunction(jcore_cb.add_polyline, 'iiii')
    ]);
    Module.ccall('phi_install_display_add_face', 'number', ['number'], [
        Module.addFunction(jcore_cb.add_face, 'iiiiii')
    ]);
    Module.ccall('phi_install_display_delete_object', 'number', ['number'], [
        Module.addFunction(jcore_cb.delete_object, 'ii')
    ]);
    Module.ccall('phi_install_display_clear_model', 'number', [], [
        Module.addFunction(jcore_cb.clear_model, 'v')
    ]);
    Module.ccall('phi_install_display_set_face_curvatures', 'number', ['number'], [
        Module.addFunction(jcore_cb.set_face_curvatures, 'iiii')
    ]);
    Module.ccall('phi_install_display_set_face_normals', 'number', ['number'], [
        Module.addFunction(jcore_cb.set_face_normals, 'iiii')
    ]);

    const set_tri_f_ptr = Module.addFunction(jcore_cb.set_STL_face_triangles, 'iiiiii');

    // Wrap core functions
    const start_move_vertices = Module.cwrap(
        'phi_start_move_vertices',
        'number',
        ['number', 'number'] // Numver of vertices and pointer to list of ids
    );

    const move_vertices = Module.cwrap(
        'phi_move_vertices',
        'number',
        [
            'number', 'number', // Number of vertices and pointer to list of vertex ids
            'number',
            'number',
            'number', // Ray base (x,y,z)
            'number',
            'number',
            'number', // Ray direction (x,y,z)
            'number',
            'number'
        ] // Ball size and hardness
    );

    const end_move_vertices = Module.cwrap(
        'phi_end_move_vertices',
        'number',
        ['number', 'number'] // Number of vertices and pointer to list of vertex ids
    );

    const start_move_vertex_on_edge = Module.cwrap(
        'phi_start_move_vertex_on_edge',
        'number',
        ['number', 'number'] // Vertex Id, Edge Id
    );

    const move_vertex_on_edge = Module.cwrap(
        'phi_move_vertex_on_edge',
        'number',
        [
            'number',
            'number', // Vertex Id and Edge Id
            'number',
            'number',
            'number', // Ray base (x,y,z)
            'number',
            'number',
            'number', // Ray direction (x,y,z)
            'number',
            'number'
        ] // Ball size and hardness
    );

    const end_move_vertex_on_edge = Module.cwrap(
        'phi_end_move_vertex_on_edge',
        'number',
        ['number', 'number'] // Vertex Id, Edge Id
    );

    const start_move_edges = Module.cwrap(
        'phi_start_move_edges',
        'number',
        ['number', 'number'] // Size of array and pointer to array of objects Ids
    );

    const move_edges = Module.cwrap(
        'phi_move_edges',
        'number',
        [
            'number',
            'number', // Size of array and pointer to array of objects Ids
            'number',
            'number',
            'number', // Translation vector (x,y,z)
            'number',
            'number',
            'number', // Axis of rotation base (x,y,z)
            'number',
            'number',
            'number', // Axis of rotation direction (x,y,z)
            'number' // Angle of rotation
            /*, 'number', 'number' */
        ] // Ball size and hardness
    );

    const end_move_edges = Module.cwrap(
        'phi_end_move_edges',
        'number',
        ['number', 'number'] // Size of array and pointer to array of objects Ids
    );

    const start_face_offset = Module.cwrap(
        'phi_start_face_offset',
        'number',
        [
            'number',
            'number', // Face and vertex id
            'number',
            'number',
            'number',
        ] // Coordinates of target point where vertex should move
    );

    const face_offset = Module.cwrap(
        'phi_face_offset',
        'number',
        [
            'number',
            'number', // Face and vertex id
            'number',
            'number',
            'number',
        ] // Coordinates of target point where vertex should move
    );

    const end_face_offset = Module.cwrap(
        'phi_end_face_offset',
        'number',
        ['number', 'number'] // Face and vertex id
    );

    const start_scale_edges = Module.cwrap(
        'phi_start_scale_edges',
        'number',
        ['number', 'number'] // Size of array and pointer to array of objects Ids
    );

    const scale_edges = Module.cwrap(
        'phi_scale_edges',
        'number',
        [
            'number', 'number', // Size of array and pointer to array of objects Ids
            'number', 'number', 'number', // Scale center vector (x,y,z)
            'number',           // Scale factor
            'number',           // Use axis flag
            'number', 'number', 'number' // Scale axis
        ]
    );

    const end_scale_edges = Module.cwrap(
        'phi_end_scale_edges',
        'number',
        ['number', 'number'] // Size of array and pointer to array of objects Ids
    );

    const get_geometry_of_edges = Module.cwrap(
        'phi_get_geometry_of_edges',
        'number',
        [
            'number', // The number of edges to be examined.
            'number', // pointer of array of edge ids
            'number', // boolean flag for high_res
            'number', // pointer to 3 element array of centre coordinates
            'number', // pointer to 3 element array of norm coords
            'number', // radius
            'number', // pointer to 3 element array for char dir coords
        ]
    ) as any as (
        number_of_edges: number,
        edges_ptr: Pointer,
        is_high_res: number,
        centre_coords_ptr: Pointer,
        norm_coords_ptr: Pointer,
        radius_ptr: Pointer,
        characteristic_dir_coords_ptr: Pointer,
    ) => number;

    const get_face_geometry = Module.cwrap(
        'phi_get_face_geometry',
        'number',
        [
            'number',
            'number',
            'number',
            'number',
            'number',
            'number',
        ]
    ) as any as (
        fid: Face,
        centre_coords: Pointer,
        norm_coords: Pointer,
        radius: Pointer,
        characteristic_dir_coords: Pointer,
        flatness: Pointer,
    ) => number;

    const get_ray_edge_intersection = Module.cwrap(
        'phi_get_ray_edge_intersection',
        'number',
        [
            'number', // Edge id
            'number', 'number', 'number', // Ray base
            'number', 'number', 'number', // Ray direction
            'number', // High res flag
            'number', // Array with length 3 with xyz coordinates of intersection point
            'number'
        ] // Parameter value
    );

    const get_face_ray_intersection = Module.cwrap(
        'phi_get_face_ray_intersection',
        'number',
        [
            'number', // Face id
            'number', 'number', 'number', // Ray base
            'number', 'number', 'number', // Ray direction
            'number', // Array with length 3 with xyz coordinates of intersection point
            'number', 'number',
        ] // Parameter values u, v
    );

    const get_face_norm_at_edge_pt = Module.cwrap(
        'phi_get_face_norm_at_edge_pt',
        'number',
        [
            'number', // Face id
            'number', // Edge id
            'number', 'number', 'number', // Ray base
            'number', 'number', 'number', // Ray direction
            'number', 'number', 'number', // Normal
        ]
    );

    const start_rotate_vertex_normal = Module.cwrap(
        'phi_start_rotate_vertex_normal',
        'number',
        ['number'] // Object Id
    );

    const rotate_vertex_normal = Module.cwrap(
        'phi_rotate_vertex_normal',
        'number',
        [
            'number', // Object Id
            'number',
            'number',
            'number', // Direction of axis of rotation (x,y,z)
            'number'
        ] // Angle of rotation
    );

    const end_rotate_vertex_normal = Module.cwrap(
        'phi_end_rotate_vertex_normal',
        'number',
        ['number'] // Object Id
    );

    const start_deform_edge = Module.cwrap(
        'phi_start_deform_edge',
        'number',
        ['number'] // Object Id
    );

    const deform_edge = Module.cwrap(
        'phi_deform_edge',
        'number',
        [
            'number', // Object Id
            'number',
            'number',
            'number', // Ray base (x,y,z)
            'number',
            'number',
            'number', // Ray direction (x,y,z)
            'number',
            'number'
        ] // Ball size and hardness
    );

    const end_deform_edge = Module.cwrap(
        'phi_end_deform_edge',
        'number',
        ['number'] // Object Id
    );

    const delete_objects = Module.cwrap(
        'phi_delete_objects',
        'number',
        ['number', 'number'] // Size of array of and pointer to array of object Ids.
    );

    const delete_body = Module.cwrap(
        'phi_delete_body',
        'number',
        ['number'] // Body id.
    );

    const add_vertex = Module.cwrap(
        'phi_add_vertex',
        'number',
        [
            'number', // Edge id
            'number',
            'number',
            'number', // Ray base (x,y,z)
            'number',
            'number',
            'number'
        ] // Ray direction (x,y,z)
    );

    const add_vertex_at_param = Module.cwrap(
        'phi_add_vertex_at_param',
        'number',
        [
            'number', // Edge id
            'number', // parameter value
        ]
    );

    const add_edge = Module.cwrap(
        'phi_add_edge',
        'number',
        [
            'number', 'number', 'number', // Face_id, vertex1_id, vertex2_id
            //    'number', 'number', 'number', // Number of samples and pointer to array of sample coords (3 per sample) and to normal coords (3 per sample)
            'number', 'number', // Number of samples and pointer to array of sample coords (3 per sample)
            'number', // is_planar flag
        ]
    );

    const find_edge_shared_vertex = Module.cwrap(
        'phi_find_edge_shared_vertex',
        'number',
        ['number', 'number'] // Edge1 Id, Edge2 Id
    );

    const is_vertex_smooth = Module.cwrap(
        'phi_is_vertex_smooth',
        'number',
        ['number'] // Vertex Id
    );

    const is_joint_smooth = Module.cwrap(
        'phi_is_joint_smooth',
        'number',
        ['number', 'number'] // Edge1 Id, Edge2 Id
    );

    const can_make_edge_smooth = Module.cwrap(
        'phi_can_make_edge_smooth',
        'number',
        ['number'] // Edge Id
    );

    const can_make_edge_nonsmooth = Module.cwrap(
        'phi_can_make_edge_nonsmooth',
        'number',
        ['number'] // Edge Id
    );

    const make_edge_smooth = Module.cwrap(
        'phi_make_edge_smooth',
        'number',
        ['number', 'number'] // Edge Id, radius
    );

    const make_edge_nonsmooth = Module.cwrap(
        'phi_make_edge_nonsmooth',
        'number',
        ['number'] // Edge Id
    );

    const add_joint_smoothness = Module.cwrap(
        'phi_add_joint_smoothness',
        'number',
        [
            'number', 'number',  // Edge1 Id, Edge2 Id
            'number', // strength
        ]
    );

    const remove_joint_smoothness = Module.cwrap(
        'phi_remove_joint_smoothness',
        'number',
        [
            'number', 'number',  // Edge1 Id, Edge2 Id
            'number', // no_geometry_change flag
        ]
    );

    const smoothen_vertices = Module.cwrap(
        'phi_smoothen_vertices',
        'number',
        [
            'number', 'number', // size of array and pointer to array of vertex ids
            'number',
        ] // radius
    );

    const remove_vertex_smoothness = Module.cwrap(
        'phi_remove_vertex_smoothness',
        'number',
        ['number', 'number'] // size of array and pointer to array of vertex ids
    );

    const smoothen_edges_as_one = Module.cwrap(
        'phi_smoothen_edges_as_one',
        'number',
        [
            'number', 'number', // Size of array of and pointer to array of edge Ids.
            'number',           // Strength
            'number', 'number', // stricly fix ders flag and fix_end_ders_override flag.
        ]
    );

    const can_smoothen_edges_as_one = Module.cwrap(
        'phi_can_smoothen_edges_as_one',
        'number',
        ['number', 'number'] // Size of array of and pointer to array of edge Ids.
    );

    const smoothen_edges = Module.cwrap(
        'phi_smoothen_edges',
        'number',
        [
            'number', 'number', // Size of edge array and pointer to array of edge Ids.
            'number', 'number', 'number' // Strength parameter and two flags 0/1
        ]
    );

    const make_edges_planar = Module.cwrap(
        'phi_make_edges_planar',
        'number',
        [
            'number',
            'number', // Size of array of and pointer to array of edge Ids.
            'number',
            'number',
            'number', // xyz coordinates of base,
            'number',
            'number',
            'number'
        ] // xyz coordinates of normal.
    );

    const inflate_face = Module.cwrap(
        'phi_inflate_face',
        'number',
        ['number', 'number'] // Face Id, nsteps
    );

    const deflate_face = Module.cwrap(
        'phi_deflate_face',
        'number',
        ['number', 'number'] // Face Id, nsteps
    );

    const pop_face = Module.cwrap(
        'phi_pop_face',
        'number',
        [
            'number',
            'number',
            'number',
            'number',
            'number',
        ]
    ) as any as (
        fid: Face,
        is_in: 1 | 0,
        dir_vec_pointer: Pointer,
        dist: number,
        pd_coords: Pointer,
    ) => number;

    const pop_faces = Module.cwrap(
        'phi_pop_faces',
        'number',
        [
            'number',
            'number',
            'number',
            'number',
            'number',
            'number',
        ]
    ) as any as (
        number_of_faces: number,
        face_ids_ptr: Pointer,
        is_in: 1 | 0,
        dir_vec_pointer: Pointer,
        dist: number,
        pd_coords: Pointer,
    ) => number;

    const make_hole_or_handle_multi = Module.cwrap(
        'phi_make_hole_or_handle_multi',
        'number',
        [
            'number', 'number',  // Size of first array of face ids and array pointer
            'number', 'number',   // Size of second array of face ids and array pointer
            'number',             // is_hole flag
            'number', 'number', // Anchor vertex Ids
        ]
    );

    const make_hole_or_handle = Module.cwrap(
        'phi_make_hole_or_handle',
        'number',
        [
            'number', 'number', 'number', // Face1 Id, face2 Id, is_hole flag
            'number', 'number', // Anchor vertex Ids
        ]
    );

    const delete_faces = Module.cwrap(
        'phi_delete_faces',
        'number',
        [
            'number',
            'number',
            'number',
        ]
    ) as any as (
        number_of_faces: number,
        face_ids: Pointer,
        first_new_face_id: Pointer,
    ) => number;

    const delete_vertex = Module.cwrap(
        'phi_delete_vertex',
        'number',
        ['number', 'number'] // Vertex Id, no_geometry_change flag
    );

    const save_model = Module.cwrap(
        'phi_save_model',
        'number',
        [
            'number', 'number',
            'number',
            'number',
        ]
    ) as any as (
        extra_data_ptr: Pointer, // Pointer to extra_data string and its length
        extra_data_len: number,
        scale_factor: number,
        DEPRECATED: null, // A string that corresponds to the model scale (deprecated and null)
    ) => number;

    const load_model = Module.cwrap(
        'phi_load_model',
        'number',
        [
            'number', 'number',
            'number',
            'number',
            'number',
            'number',
            'number', 'number',
            'number',
            'number',
        ]
    ) as any as (
        buffer_ptr: Pointer, // Buffer and buffer length
        buffer_len: number,
        into_current: 1 | 0, // 'Into current' flag
        additional_scale_factor: number, // Additional_scale_factor
        DEPRECATED: null, // Workspace unit string (deprecated and null)
        no_id_rehash: 1 | 0, // If set, forces load to NOT rehash world ids on occasions
                             // where it normally would (e.g. loading a world into
                             // current). USE WITH CARE, leave at 0 if unsure.
        extra_data_ptr: Pointer, // pointer to returned extra data, and pointer to length of returned extra data.
        extra_data_len_ptr: Pointer,
        output_buffer_ptr: Pointer, // pointer to returned model scaling factor
        DEPRECATED2: Pointer, // pointer to model units string (deprecated)
    ) => number;

    const get_world_scaling_factor = Module.cwrap(
        'phi_get_world_scaling_factor',
        'number',
        [] // No parameters.
    );

    const set_world_scaling_factor = Module.cwrap(
        'phi_set_world_scaling_factor',
        'number',
        ['number']         // Factor
    );

    const set_add_edge_pair_tangencies_on_load = Module.cwrap(
        'phi_set_add_edge_pair_tangencies_on_load',
        'number',
        ['number']         // on/off
    );

    const undo = Module.cwrap(
        'phi_undo',
        'number',
        [] // No arguments
    );

    const redo = Module.cwrap(
        'phi_redo',
        'number',
        [] // No arguments
    );

    const can_undo = Module.cwrap(
        'phi_can_undo',
        'number',
        [] // No arguments
    );

    const can_redo = Module.cwrap(
        'phi_can_redo',
        'number',
        [] // No arguments
    );

    const can_inflate_face = Module.cwrap(
        'phi_can_inflate_face',
        'number',
        ['number'] // Face id
    );

    const can_deflate_face = Module.cwrap(
        'phi_can_deflate_face',
        'number',
        ['number'] // Face id
    );

    const get_vertex_normal = Module.cwrap(
        'phi_get_vertex_normal',
        'number',
        [
            'number', // Vertex id
            'number',
        ] // Buffer of known length 3, a return value (the x, y, and z components of the vector).
    );

    const get_face_curvatures = Module.cwrap(
        'phi_get_face_curvatures',
        'number',
        ['number'] // Face id
    );

    const get_face_normals = Module.cwrap(
        'phi_get_face_normals',
        'number',
        ['number'] // Face id
    );

    const translate_faces_to_nurbs = Module.cwrap(
        'phi_translate_faces_to_nurbs',
        'number',
        [
            'number', // Number of faces
            'number'
        ] // Pointer to array of face ids
    );

    const get_edge_num_of_face = Module.cwrap(
        'phi_get_edge_num_of_face',
        'number',
        [
            'number', // Face id
            'number', // Buffer of known length 1, a return value (the number of edges).
        ]
    );

    const get_edge_num_of_vertex = Module.cwrap(
        'phi_get_edge_num_of_vertex',
        'number',
        [
            'number', // Vertex id
            'number', // Buffer of known length 1, a return value (the number of edges).
        ]
    );

    const get_edges_of_face = Module.cwrap(
        'phi_get_edges_of_face',
        'number',
        [
            'number', // Face id
            'number', // The (known) number of edges on this face
            'number', // Buffer of known length, containing the edge ids
        ]
    );

    const get_edges_of_vertex = Module.cwrap(
        'phi_get_edges_of_vertex',
        'number',
        [
            'number', // Vertex id
            'number', // The (known) number of edges on this face
            'number', // Buffer of known length, containing the edge ids
        ]
    );

    const get_faces_of_edge = Module.cwrap(
        'phi_get_faces_of_edge',
        'number',
        [
            'number', // Edge id
            'number', // Two buffers of known length 1, containing the two face ids
            'number'
        ]
    );

    const get_vertices_of_edge = Module.cwrap(
        'phi_get_vertices_of_edge',
        'number',
        [
            'number', // Edge id
            'number', // Two buffers of known length 1, containing the two vertex ids
            'number',
        ]
    );

    const move_vertex_to_coords = Module.cwrap(
        'phi_move_vertex_to_coords',
        'number',
        [
            'number', // Vertex id
            'number',
            'number',
            'number', // The x, y, z, coordinates for the vertex
            'number', // The ball size and hardness
            'number'
        ]
    );

    const make_edges_linear = Module.cwrap(
        'phi_make_edges_linear',
        'number',
        ['number', 'number', 'number'] // Size of array of and pointer to array of edge Ids, and flag to force.
    );

    const make_edges_linear_as_one = Module.cwrap(
        'phi_make_edges_linear_as_one',
        'number',
        ['number', 'number', 'number'] // Size of array of and pointer to array of edge Ids, and flag to force.
    );


    const make_edges_nonlinear = Module.cwrap(
        'phi_make_edges_nonlinear',
        'number',
        ['number', 'number'] // Size of array of and pointer to array of edge Ids.
    );

    const make_edges_circular = Module.cwrap(
        'phi_make_edges_circular',
        'number',
        [
            'number',
            'number', // Size of array of and pointer to array of edge Ids.
            'number',
            'number', // Size of array of and pointer to array of vertex Ids.
            'number'
        ] // Center vertex id
    );

    const can_make_edges_symmetric = Module.cwrap(
        'phi_can_make_edges_symmetric',
        'number',
        [
            'number',
            'number', // Two edge Ids.
            'number',
            'number',
            'number', // xyz coordinates of symmetry plane base.
            'number',
            'number',
            'number'
        ] // xyz coordinates of symmetry plane normal.
    );

    const make_edges_symmetric = Module.cwrap(
        'phi_make_edges_symmetric',
        'number',
        [
            'number',
            'number', // Two edge Ids.
            'number',
            'number',
            'number', // xyz coordinates of symmetry plane base.
            'number',
            'number',
            'number'
        ] // xyz coordinates of symmetry plane normal.
    );

    const can_make_edges_circular = Module.cwrap(
        'phi_can_make_edges_circular',
        'number',
        [
            'number',
            'number', // Size of array of and pointer to array of edge Ids.
            'number',
            'number', // Size of array of and pointer to array of vertex Ids.
            'number'
        ] // Center vertex id
    );

    const can_make_face_circular = Module.cwrap(
        'phi_can_make_face_circular',
        'number',
        ['number'] // Face Id.
    );

    const make_face_circular = Module.cwrap(
        'phi_make_face_circular',
        'number',
        ['number'] // Face Id.
    );

    const set_vertex_distance = Module.cwrap(
        'phi_set_vertex_distance',
        'number',
        [
            'number',
            'number', // Ids of two vertices.
            'number', // Target distance
        ]
    );

    const triangulate_faces = Module.cwrap(
        'phi_triangulate_faces',
        'number',
        [
            'number',
            'number', // Size of array of and pointer to array of vertex Ids.
            'number', // Number of triangles.
            'number'
        ] // Pointer to callback function.
    );

    const set_display_face_triangulation_res = Module.cwrap(
        'phi_set_display_face_triangulation_res',
        'number',
        ['number'] // Number of triangles.
    );

    const set_display_face_triangulation_res_for_drag = Module.cwrap(
        'phi_set_display_face_triangulation_res_for_drag',
        'number',
        ['number'] // Number of triangles.
    );

    const get_display_face_triangulation_res = Module.cwrap(
        'phi_get_display_face_triangulation_res',
        'number',
        [] // No parameters.
    );

    const get_display_face_triangulation_res_for_drag = Module.cwrap(
        'phi_get_display_face_triangulation_res_for_drag',
        'number',
        [] // No parameters.
    );

    const set_face_tessel = Module.cwrap(
        'phi_set_face_tessel',
        'number',
        ['number'] // On or off flag.
    );

    const set_global_fairing_strength = Module.cwrap(
        'phi_set_global_fairing_strength',
        'number',
        ['number'] // Strength.
    );

    const get_global_fairing_strength = Module.cwrap(
        'phi_get_global_fairing_strength',
        'number',
        [] // No parameters.
    );

    const is_vertex_of_face = Module.cwrap(
        'phi_is_vertex_of_face',
        'number',
        ['number', 'number'] // Vertex Id, Face Id
    );

    const is_vertex_of_edge = Module.cwrap(
        'phi_is_vertex_of_edge',
        'number',
        ['number', 'number'] // Vertex Id, Edge Id
    );

    const get_point_at_edge_param = Module.cwrap(
        'phi_get_point_at_edge_param',
        'number',
        [
            'number', // Edge id,
            'number', // parameter u
            'number'  // Buffer of known length 3, a return value (the x, y, and z components of the point).
        ]
    );

    const get_tangent_at_edge_param = Module.cwrap(
        'phi_get_tangent_at_edge_param',
        'number',
        [
            'number', // Edge id,
            'number', // parameter u
            'number'  // Buffer of known length 3, a return value (the x, y, and z components of the tangent).
        ]
    );

    const is_edge_linear = Module.cwrap(
        'phi_is_edge_linear',
        'number',
        ['number'] // Edge Id
    );

    const is_edge_linear_geometry = Module.cwrap(
        'phi_is_edge_linear_geometry',
        'number',
        ['number'] // Edge Id
    );


    const set_undo_tracking = Module.cwrap(
        'phi_set_undo_tracking',
        'number',
        ['number'] // on/off flag
    );

    const save_undo_state = Module.cwrap(
        'phi_save_undo_state',
        'number',
        [] // No arguments
    );

    const start_compound_operation = Module.cwrap(
        'phi_start_compound_operation',
        'number',
        [] // No arguments
    );

    const end_compound_operation = Module.cwrap(
        'phi_end_compound_operation',
        'number',
        [] // No arguments
    );

    const get_edge_curve_freedom = Module.cwrap(
        'phi_get_edge_curve_freedom',
        'number',
        ['number'] // Edge Id.
    );

    const set_curve_freedom_of_edges = Module.cwrap(
        'phi_set_curve_freedom_of_edges',
        'number',
        [
            'number', 'number', // Size of array of and pointer to array of edge Ids.
            'number', // freedom value
        ]
    );

    const set_edge_curve_freedom_default = Module.cwrap(
        'phi_set_edge_curve_freedom_default',
        'number',
        ['number'] // default freedom value
    );

    const is_object_visible = Module.cwrap(
        'phi_is_object_visible',
        'number',
        ['number'] // Object Id
    );

    const is_object_fixed = Module.cwrap(
        'phi_is_object_fixed',
        'number',
        ['number'] // Object Id
    );

    const change_object_fixedness = Module.cwrap(
        'phi_change_object_fixedness',
        'number',
        [
            'number', 'number', // Size of array of and pointer to array of object Ids.
            'number',
        ] // fixed flag
    );

    const change_object_visibility = Module.cwrap(
        'phi_change_object_visibility',
        'number',
        [
            'number', 'number', // Size of array of and pointer to array of object Ids.
            'number', // visible flag
        ]
    );

    const get_body_num_of_model = Module.cwrap(
        'phi_get_body_num_of_model',
        'number',
        ['number'] // Pointer to return number of bodies
    );

    const get_bodies_of_model = Module.cwrap(
        'phi_get_bodies_of_model',
        'number',
        ['number', 'number'] // Size of array and pointer to array of model bodies
    );

    const is_body_open = Module.cwrap(
        'phi_is_body_open',
        'number',
        ['number'] // Body id
    );

    const calc_ray_dir_for_face_sampling = Module.cwrap(
        'phi_calc_ray_dir_for_face_sampling',
        'number',
        [
            'number', // Face id
            'number', // Vertex 1 id
            'number', // Vertex 2 id
            'number', 'number', 'number',
        ] // Pointers to direction (x,y,z) coords
    );

    const modify_edge_geometry = Module.cwrap(
        'phi_modify_edge_geometry',
        'number',
        [
            'number', // Edge id
            'number', // Vertex 1 id
            'number', // Vertex 2 id
            'number', 'number',
        ] // number of samples and array of sample coordinates
    );

    const set_face_colour = Module.cwrap(
        'phi_set_face_colour',
        'number',
        [
            'number', 'number', // Length and pointer to array of face ids
            'number', 'number', 'number', // RGB values
        ]
    );

    const get_face_colour = Module.cwrap(
        'phi_get_face_colour',
        'number',
        [
            'number', // Face id
            'number', 'number', 'number',
        ] // RGB values
    );

    const set_face_material = Module.cwrap(
        'phi_set_face_material',
        'number',
        [
            'number', 'number', // Length and pointer to array of face ids
            'number', // String buffer (should be null terminated)
        ]
    );

    const get_face_material = Module.cwrap(
        'phi_get_face_material',
        'number',
        ['number'] // Face id
    );

    const set_body_label = Module.cwrap(
        'phi_set_body_label',
        'number',
        [
            'number', 'number', // Size of array of body ids and pointer to array
            'number', 'number', // String buffer and buffer length
        ]
    );

    const get_body_label = Module.cwrap(
        'phi_get_body_label',
        'number',
        ['number'] // Body id
    );

    const set_face_label = Module.cwrap(
        'phi_set_face_label',
        'number',
        [
            'number', 'number', // Size of array of face ids and pointer to array
            'number', 'number', // String buffer and buffer length
        ]
    );

    const get_face_label = Module.cwrap(
        'phi_get_face_label',
        'number',
        ['number'] // Face id
    );

    const get_sedge_num_of_face = Module.cwrap(
        'phi_get_sedge_num_of_face',
        'number',
        [
            'number', // Id of face
            'number', // Number of superedges
        ]
    );

    const get_sedges_of_face = Module.cwrap(
        'phi_get_sedges_of_face',
        'number',
        [
            'number', // Id of face
            'number', // pointer for output
        ]
    ) as any as (
        fid: Face,
        sedges_out_ptr: Pointer,
    ) => number;

    const get_edge_length = Module.cwrap(
        'phi_get_edge_length',
        'number',
        [
            'number',
            'number',
        ]
    ) as any as (
        eid: Edge,
        length: Pointer,
    ) => number;

    const get_face_crosshair_at_params = Module.cwrap(
        'phi_get_face_crosshair_at_params',
        'number',
        [
            'number',            // Face id
            'number', 'number',   // u, v
            'number', 'number', // Array of xhair coords (4*3 elements) and array of xhair edge ids (4 elements)
        ]
    );

    const get_body_of_vertex = Module.cwrap(
        'phi_get_body_of_vertex',
        'number',
        [
            'number',
            'number',
        ]
    ) as any as (
        vid: Vertex,
        body_ptr: Pointer,
    ) => number;

    const get_body_of_edge = Module.cwrap(
        'phi_get_body_of_edge',
        'number',
        [
            'number',
            'number',
        ]
    ) as any as (
        eid: Edge,
        body_ptr: Pointer,
    ) => number;

    const get_body_of_face = Module.cwrap(
        'phi_get_body_of_face',
        'number',
        [
            'number',
            'number',
        ]
    ) as any as (
        fid: Face,
        body_ptr: Pointer,
    ) => number;

    const listify_edges = Module.cwrap(
        'phi_listify_edges',
        'number',
        [
            'number', 'number', // Size of array and pointer to array of objects Ids
            'number', 'number', // Pointers to flags for is_open and is_closed
            'number', // Pointer to array of sorted edges to be returned (size is same as input array)
        ]
    );

    const get_face_connected_component_info = Module.cwrap(
        'phi_get_face_connected_component_info',
        'number',
        [
            'number', 'number', // Size of array and pointer to array of face Ids
            'number', 'number', // Pointers to flags for is_one_cc and is_mult_cc
            'number', 'number', // Size of array and pointer to array of boundary edge ids
            'number', 'number', // Size of array and pointer to array of boundary vertex ids
        ]
    );

    const get_face_connected_components = Module.cwrap(
        'phi_get_face_connected_components',
        'number',
        [
            'number', 'number', // Size of array of faces and pointer to array of face Ids
            'number', // ncc
            'number', 'number', // pointer to cc_count and pointer cc_ids arrays
        ]
    );

    const set_save_model_compression_level = Module.cwrap(
        'phi_set_save_model_compression_level',
        'number',
        ['number'] // Compression level
    );

    const copy_body = Module.cwrap(
        'phi_copy_body',
        'number',
        ['number'] // Body Id
    );

    const get_edge_curvatures = Module.cwrap(
        'phi_get_edge_curvatures',
        'number',
        [
            'number', 'number', // Edge ID and number of samples
            'number', 'number', // Pointer to array of vector bases amd pointer to array of vector coordinates
        ]
    );

    const merge_bodies_on_edge = Module.cwrap(
        'phi_merge_bodies_on_edge',
        'number',
        [
            'number', 'number', // Body IDs
            'number', 'number', // Edge IDs
            'number', 'number', // Vertex IDs
        ]
    );

    const get_face_triangulation_info = Module.cwrap(
        'phi_get_face_triangulation_info',
        'number',
        [
            'number', // Face id
            'number', 'number', // Pointers to number of triangulation points and number of triangles.
        ]
    );

    const get_face_triangulation = Module.cwrap(
        'phi_get_face_triangulation',
        'number',
        [
            'number', // Face id
            'number', 'number', // Number of triangulation points and number of triangles.
            'number', 'number', // Pointers to arrays of coordinates and triangle triplets.
        ]
    );

    const get_edge_planes_from_point_intersection = Module.cwrap(
        'phi_get_edge_planes_from_point_intersection',
        'number',
        [
            'number',                   // Edge id
            'number', 'number', 'number', // x,y,z coordinates of vertex
            'number',                     // High-res flag
            'number', 'number', 'number', // Pointers to variables to receive three parameter values.
        ]
    );

    const smoothen_edge_wrt_face = Module.cwrap(
        'phi_smoothen_edge_wrt_face',
        'number',
        ['number', 'number', 'number'] // Edge id, Face id, Vertex id
    );

    const flip_body_inside_out = Module.cwrap(
        'phi_flip_body_inside_out',
        'number',
        ['number'] // Body id
    );

    const set_auto_add_tangencies = Module.cwrap(
        'phi_set_auto_add_tangencies',
        'number',
        ['number'] // On flag
    );

    const set_edge_tangent_at_vertex = Module.cwrap(
        'phi_set_edge_tangent_at_vertex',
        'number',
        [
            'number', 'number',           // int eid, int vid,
            'number', 'number', 'number', // float tx, float ty, float tz
        ]
    );

    const get_edge_tangent_at_vertex = Module.cwrap(
        'phi_get_edge_tangent_at_vertex',
        'number',
        [
            'number', 'number',           // int eid, int vid,
            'number', 'number', 'number'   // float *tx, float *ty, float *tz
        ]
    );

    const set_edge_fixing_of_tangent_at_vertex = Module.cwrap(
        'phi_set_edge_fixing_of_tangent_at_vertex',
        'number',
        [
            'number', 'number',   // int eid, int vid,
            'number',             // int fix
        ]
    );

    const get_edge_fixing_of_tangent_at_vertex = Module.cwrap(
        'phi_get_edge_fixing_of_tangent_at_vertex',
        'number',
        [
            'number', 'number', // int eid, int vid
            'number', // int *fix
        ]
    );

    const smoothen_chain_network = Module.cwrap(
        'phi_smoothen_chain_network',
        'number',
        [
            'number', 'number',    // Size of edge array and pointer to array of edge Ids.
            'number', 'number'
        ]   // graph distance and strength
    );

    const is_joint_geom_smooth = Module.cwrap(
        'phi_is_joint_geom_smooth',
        'number',
        ['number', 'number'] // int e1id, int e2id
    );

    const get_core_version_info = Module.cwrap(
        'phi_get_core_version_info',
        'number',
        []
    );

    debug_additional_initializations(Module);

    return {
        add_edge,
        add_vertex_at_param,
        add_vertex,
        calc_ray_dir_for_face_sampling,
        can_deflate_face,
        can_inflate_face,
        can_make_edge_nonsmooth,
        can_make_edge_smooth,
        can_make_edges_circular,
        can_make_face_circular,
        can_redo,
        can_smoothen_edges_as_one,
        can_undo,
        change_object_fixedness,
        change_object_visibility,
        copy_body,
        deflate_face,
        deform_edge,
        delete_body,
        delete_faces,
        delete_objects,
        delete_vertex,
        end_compound_operation,
        end_deform_edge,
        end_face_offset,
        end_move_edges,
        end_move_vertex_on_edge,
        end_move_vertices,
        end_rotate_vertex_normal,
        end_scale_edges,
        face_offset,
        find_edge_shared_vertex,
        flip_body_inside_out,
        get_bodies_of_model,
        get_body_num_of_model,
        get_body_of_edge,
        get_body_of_face,
        get_body_of_vertex,
        get_core_version_info,
        get_display_face_triangulation_res_for_drag,
        get_display_face_triangulation_res,
        get_edge_curvatures,
        get_edge_curve_freedom,
        get_edge_fixing_of_tangent_at_vertex,
        get_edge_length,
        get_edge_num_of_face,
        get_edge_num_of_vertex,
        get_edge_planes_from_point_intersection,
        get_edge_tangent_at_vertex,
        get_edges_of_face,
        get_edges_of_vertex,
        get_face_colour,
        get_face_connected_component_info,
        get_face_connected_components,
        get_face_crosshair_at_params,
        get_face_curvatures,
        get_face_geometry,
        get_body_label,
        get_face_label,
        get_face_material,
        get_face_norm_at_edge_pt,
        get_face_normals,
        get_face_ray_intersection,
        get_face_triangulation_info,
        get_face_triangulation,
        get_faces_of_edge,
        get_geometry_of_edges,
        get_point_at_edge_param,
        get_ray_edge_intersection,
        get_sedge_num_of_face,
        get_sedges_of_face,
        get_tangent_at_edge_param,
        get_vertex_normal,
        get_vertices_of_edge,
        get_world_scaling_factor,
        inflate_face,
        is_body_open,
        is_edge_linear_geometry,
        is_edge_linear,
        is_joint_geom_smooth,
        is_joint_smooth,
        is_object_fixed,
        is_object_visible,
        is_vertex_of_edge,
        is_vertex_of_face,
        is_vertex_smooth,
        listify_edges,
        load_model,
        make_edge_nonsmooth,
        make_edge_smooth,
        make_edges_circular,
        make_edges_linear_as_one,
        make_edges_linear,
        make_edges_nonlinear,
        make_edges_planar,
        can_make_edges_symmetric,
        make_edges_symmetric,
        make_face_circular,
        make_hole_or_handle_multi,
        make_hole_or_handle,
        merge_bodies_on_edge,
        modify_edge_geometry,
        move_edges,
        move_vertex_on_edge,
        move_vertex_to_coords,
        move_vertices,
        pop_face,
        pop_faces,
        redo,
        remove_vertex_smoothness,
        rotate_vertex_normal,
        save_model,
        save_undo_state,
        scale_edges,
        set_add_edge_pair_tangencies_on_load,
        set_auto_add_tangencies,
        set_curve_freedom_of_edges,
        set_display_face_triangulation_res_for_drag,
        set_display_face_triangulation_res,
        set_face_tessel,
        set_global_fairing_strength,
        get_global_fairing_strength,
        set_edge_curve_freedom_default,
        set_edge_fixing_of_tangent_at_vertex,
        set_edge_tangent_at_vertex,
        set_face_colour,
        set_body_label,
        set_face_label,
        set_face_material,
        set_save_model_compression_level,
        set_tri_f_ptr,
        set_undo_tracking,
        set_vertex_distance,
        set_world_scaling_factor,
        smoothen_chain_network,
        smoothen_edge_wrt_face,
        smoothen_edges_as_one,
        smoothen_edges,
        smoothen_vertices,
        start_compound_operation,
        start_deform_edge,
        start_face_offset,
        start_move_edges,
        start_move_vertex_on_edge,
        start_move_vertices,
        start_rotate_vertex_normal,
        start_scale_edges,
        add_joint_smoothness,
        remove_joint_smoothness,
        translate_faces_to_nurbs,
        triangulate_faces,
        undo,
    };
};
